// core/action/userActions.js
import { userAPI, roleAPI, permissionAPI } from './api';

export const userActions = {
    // Get all users
    getUsers: async (params = {}) => {
        try {
            const response = await userAPI.getAll(params);
            
            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || 1
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to fetch users',
                data: []
            };
        } catch (error) {
            console.error('Error fetching users:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch users',
                data: []
            };
        }
    },

    // Get single user
    getUser: async (id) => {
        try {
            const response = await userAPI.get(id);
            
            if (response && response.success) {
                return {
                    success: true,
                    data: response.data
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to fetch user'
            };
        } catch (error) {
            console.error('Error fetching user:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch user'
            };
        }
    },

    // Create user
    createUser: async (userData) => {
        try {
            // Validate required fields
            if (!userData.name || !userData.name.trim()) {
                return { success: false, message: 'Name is required' };
            }
            if (!userData.email || !userData.email.trim()) {
                return { success: false, message: 'Email is required' };
            }
            if (!userData.password) {
                return { success: false, message: 'Password is required' };
            }
            if (userData.password.length < 6) {
                return { success: false, message: 'Password must be at least 6 characters' };
            }

            const response = await userAPI.create(userData);

            if (response && response.success) {
                return {
                    success: true,
                    data: response.data,
                    message: response.message || 'User created successfully'
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to create user'
            };
        } catch (error) {
            console.error('Error creating user:', error);
            return {
                success: false,
                message: error.message || 'Failed to create user'
            };
        }
    },

    // Update user
updateUser: async (userData) => {
    try {
        if (!userData.id) {
            return { success: false, message: 'User ID is required' };
        }

        // If password is being updated by admin, we need to handle it specially
        if (userData.password && userData.password_changed_by_admin) {
            console.log('Admin updating password for user:', userData.id);
            // You might want to log this action for audit purposes
        }

        const response = await userAPI.update(userData);

        if (response && response.success) {
            let message = response.message || 'User updated successfully';
            
            // Add specific message if password was changed
            if (userData.password && userData.password_changed_by_admin) {
                message = 'User updated and password reset successfully';
            }
            
            return {
                success: true,
                data: response.data,
                message: message
            };
        }
        return {
            success: false,
            message: response?.message || 'Failed to update user'
        };
    } catch (error) {
        console.error('Error updating user:', error);
        return {
            success: false,
            message: error.message || 'Failed to update user'
        };
    }
},

    // Delete user
    deleteUser: async (id) => {
        try {
            if (!id) {
                return { success: false, message: 'User ID is required' };
            }

            const response = await userAPI.delete({ id });

            if (response && response.success) {
                return {
                    success: true,
                    message: response.message || 'User deleted successfully'
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to delete user'
            };
        } catch (error) {
            console.error('Error deleting user:', error);
            return {
                success: false,
                message: error.message || 'Failed to delete user'
            };
        }
    },

    // Get all roles
    getRoles: async (params = {}) => {
        try {
            const response = await roleAPI.getAll(params);
            
            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || []
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to fetch roles',
                data: []
            };
        } catch (error) {
            console.error('Error fetching roles:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch roles',
                data: []
            };
        }
    },

    // Create role
    createRole: async (roleData) => {
        try {
            if (!roleData.role_name || !roleData.role_name.trim()) {
                return { success: false, message: 'Role name is required' };
            }

            // Get current user ID from localStorage if needed
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            
            // Add created_by if you want to track who created the role
            const dataWithUser = {
                ...roleData,
                created_by: user.id || 1 // Default to 1 if no user found
            };

            console.log('Sending role data:', dataWithUser); // Debug log

            const response = await roleAPI.create(dataWithUser);
            
            console.log('Role create response:', response); // Debug log

            if (response && response.success === true) {
                return {
                    success: true,
                    data: response.data,
                    message: response.message || 'Role created successfully'
                };
            } else {
                return {
                    success: false,
                    message: response?.message || 'Failed to create role'
                };
            }
        } catch (error) {
            console.error('Error creating role:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to create role'
            };
        }
    },

    // Update role
    updateRole: async (roleData) => {
        try {
            if (!roleData.id) {
                return { success: false, message: 'Role ID is required' };
            }

            // Get current user ID from localStorage if needed
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            
            // Add updated_by if you want to track who updated the role
            const dataWithUser = {
                ...roleData,
                updated_by: user.id || 1 // Default to 1 if no user found
            };

            console.log('Sending update role data:', dataWithUser); // Debug log

            const response = await roleAPI.update(dataWithUser);
            
            console.log('Role update response:', response); // Debug log

            if (response && response.success === true) {
                return {
                    success: true,
                    data: response.data,
                    message: response.message || 'Role updated successfully'
                };
            } else {
                return {
                    success: false,
                    message: response?.message || 'Failed to update role'
                };
            }
        } catch (error) {
            console.error('Error updating role:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to update role'
            };
        }
    },

    // Delete role
    deleteRole: async (id) => {
        try {
            if (!id) {
                return { success: false, message: 'Role ID is required' };
            }

            const response = await roleAPI.delete({ id });

            if (response && response.success) {
                return {
                    success: true,
                    message: response.message || 'Role deleted successfully'
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to delete role'
            };
        } catch (error) {
            console.error('Error deleting role:', error);
            return {
                success: false,
                message: error.message || 'Failed to delete role'
            };
        }
    },

    // Get all permissions
    getPermissions: async () => {
        try {
            const response = await permissionAPI.getAll();
            
            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || {},
                    flat: response.flat || []
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to fetch permissions',
                data: {}
            };
        } catch (error) {
            console.error('Error fetching permissions:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch permissions',
                data: {}
            };
        }
    },

    // Get role permissions
    getRolePermissions: async (roleId) => {
        try {
            const response = await roleAPI.getPermissions(roleId);
            
            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || []
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to fetch role permissions',
                data: []
            };
        } catch (error) {
            console.error('Error fetching role permissions:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch role permissions',
                data: []
            };
        }
    },

    // Get current user permissions
    getCurrentUserPermissions: () => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return [];
            
            const user = JSON.parse(userStr);
            return user.permissions || [];
        } catch (error) {
            console.error('Error getting user permissions:', error);
            return [];
        }
    },

    // Check if user has permission
    hasPermission: (permissionSlug) => {
        const permissions = userActions.getCurrentUserPermissions();
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        // Super admin has all permissions
        if (user.role_slug === 'super-admin') return true;
        
        return permissions.includes(permissionSlug);
    },

    // Check if user has any of the given permissions
    hasAnyPermission: (permissions) => {
        const userPermissions = userActions.getCurrentUserPermissions();
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        if (user.role_slug === 'super-admin') return true;
        
        return permissions.some(p => userPermissions.includes(p));
    },

    // Check if user has all permissions
    hasAllPermissions: (permissions) => {
        const userPermissions = userActions.getCurrentUserPermissions();
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        if (user.role_slug === 'super-admin') return true;
        
        return permissions.every(p => userPermissions.includes(p));
    },

    // Get user role
    getUserRole: () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.role_slug || null;
    },

    // Check if user is super admin
    isSuperAdmin: () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.role_slug === 'super-admin';
    },

    // Check if user is admin
    isAdmin: () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.role_slug === 'super-admin' || user.role_slug === 'admin';
    }
};

export default userActions;